export interface RouteResult {
  distance: number;
  duration: number;
  path: Array<[number, number]>;
}

export class RoutePlanError extends Error {
  constructor(message: string, readonly code = "") {
    super(message);
    this.name = "RoutePlanError";
  }
}

interface CachedRoute {
  expiresAt: number;
  value: RouteResult;
}

const cache = new Map<string, CachedRoute>();
const inFlight = new Map<string, Promise<RouteResult>>();
let queue: Promise<unknown> = Promise.resolve();
let lastRequestAt = 0;
let blockedUntil = 0;

const MIN_REQUEST_INTERVAL_MS = 750;
const CACHE_TTL_MS = 60 * 60 * 1000;
const RATE_LIMIT_COOLDOWN_MS = 30 * 1000;

export function routeRequestKey(mode: string, start: [number, number], end: [number, number]) {
  const coord = (value: [number, number]) => value.map((part) => part.toFixed(6)).join(",");
  return `${mode}:${coord(start)}>${coord(end)}`;
}

export function getRouteCooldownMs(now = Date.now()) {
  return Math.max(0, blockedUntil - now);
}

export function requestRoute(key: string, load: () => Promise<RouteResult>): Promise<RouteResult> {
  const now = Date.now();
  const cached = cache.get(key);
  if (cached && cached.expiresAt > now) return Promise.resolve(cached.value);
  if (cached) cache.delete(key);
  const pending = inFlight.get(key);
  if (pending) return pending;

  const request = queue.then(async () => {
    const cooldown = getRouteCooldownMs();
    if (cooldown) throw new RoutePlanError("CUQPS_HAS_EXCEEDED_THE_LIMIT", "10021");
    const delay = Math.max(0, MIN_REQUEST_INTERVAL_MS - (Date.now() - lastRequestAt));
    if (delay) await wait(delay);
    lastRequestAt = Date.now();
    try {
      const value = await load();
      cache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
      return value;
    } catch (error) {
      if (error instanceof RoutePlanError && error.code === "10021") blockedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
      throw error;
    }
  });
  queue = request.catch(() => undefined);
  inFlight.set(key, request);
  request.finally(() => inFlight.delete(key)).catch(() => undefined);
  return request;
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, milliseconds));
}
